/**
 * lib/agent/effort-levels.ts — every effort picker's vocabulary, per vendor.
 *
 * FOUNDER, 2026-10-04 (verbatim Russian quote in docs/canon/
 * FOUNDER_DECISIONS_INDEX.md; this UI tree is English-only): the effort levels
 * in the GPT picker are not named the way OpenAI names them, which is wrong —
 * they must carry OpenAI's own names, Claude's must carry Anthropic's, and it
 * has to be consistent.
 *
 * Until this file, ONE ladder (Ultracode / Max / Extra high / High / Medium /
 * Low, written in session-chat-pane.tsx and again in the role editor) served
 * every vendor, and the gateway silently turned the Claude-only Max and
 * Ultracode into codex `xhigh` — a GPT picker that showed one level and sent
 * another. Now each vendor's picker shows exactly that vendor's names, offers
 * only that vendor's levels, and only the ones the selected model supports;
 * the value saved and sent is exactly the vendor's value.
 *
 * WHERE THE TRUTH COMES FROM (measured 2026-10-04 on chaos/agent:latest,
 * claude 2.1.287 · codex 0.160.0 · gemini 0.62.0; transcript in
 * EFFORT_LABELS_GPT_RENAME_RECEIPT.md):
 *
 *   GPT     DATA FIRST. The codex app-server `model/list` the gateway already
 *           calls returns, per model, `supportedReasoningEfforts`
 *           [{reasoningEffort, description}] and `defaultReasoningEffort`.
 *           The gateway carries both onto each catalogue row (`efforts`,
 *           `defaultEffort`), so a level OpenAI adds or drops shows up the
 *           same day with no code change. Labels are the codex TUI's own
 *           ("Select Reasoning Level for GPT-6.1-Sol": Low · Medium · High ·
 *           Extra high, then "Advanced Reasoning": Max · Ultra); a value the
 *           table below does not know yet is shown as the vendor's token,
 *           capitalised, rather than hidden.
 *           GROUPING (founder, 2026-10-04 16:41Z; the verbatim Russian quote
 *           is in docs/canon/FOUNDER_DECISIONS_INDEX.md: GPT has no plain Max,
 *           re-check it, and the default is Extra high): codex 0.160.0's TUI
 *           lists Low · Medium · High · Extra high (the default for CHAOS), then
 *           a separate "More reasoning… — Max and Ultra consume usage limits
 *           faster" that opens "Advanced Reasoning" with the warning "Consumes
 *           usage limits faster". The pickers mirror that: Max and Ultra are a
 *           separate "More reasoning" group below Extra high, with the
 *           vendor's warning and descriptions, shown only for a model whose
 *           vendor data has them (GPT-6-Luna: Max only; GPT-5.5: none). The
 *           default stays Extra high and nothing selects Max/Ultra for GPT on
 *           its own (see storedEffortForVendor).
 *   Claude  No per-model list reaches the gateway, so the ladder is pinned to
 *           the CLI's own picker (`/model`: "Low effort" … "xHigh effort" …
 *           "Max effort"; `/effort` values low|medium|high|xhigh|max, with the
 *           `/effort` help's own one-line descriptions) and the per-model gaps
 *           and defaults the bundle hard-codes (@chaos/types/effort). The live
 *           picker's "Effort not supported for <model>" verdict arrives as
 *           `efforts: []` and wins.
 *           ULTRACODE IS NOT A LEVEL (2026-10-04 review). Since claude 2.1.287
 *           it is its own toggle in `/effort` that "no longer forces xhigh
 *           effort and stays on at any effort level". CHAOS stores it as the
 *           effort value `ultracode` and passes no --effort with it, so the CLI
 *           runs the model's default level. The pickers therefore show the
 *           Low…Max ladder and, beside it, an Ultracode toggle with the level
 *           it really runs at — never Ultracode as a rung above Max.
 *   Gemini  The CLI has no effort concept at all (no flag, no picker; its
 *           model configs hard-code thinkingLevel HIGH) — no options.
 *
 * WHICH LEVEL IS SHOWN = WHICH LEVEL IS SENT. effectiveEffort() below and the
 * gateway's effortForRun() both call @chaos/types/effort resolveRunEffort:
 * the stored level if this model has it, else (Claude) the CLI's own max/xhigh
 * → high downgrade, else the system default (SYSTEM_DEFAULT_EFFORT: Claude
 * max, GPT xhigh — what the gateway runs when nothing is stored), else the
 * model's own default.
 *
 * Pinned by tests/unit/effort-levels.test.ts against that evidence.
 */
import {
  ANTHROPIC_EFFORT_LEVELS,
  OPENAI_ADVANCED_EFFORTS,
  OPENAI_BUNDLED_DEFAULT_MODEL,
  OPENAI_BUNDLED_EFFORTS,
  SYSTEM_DEFAULT_EFFORT,
  ULTRACODE,
  anthropicEffortTable,
  isOpenaiAdvancedEffort,
  isUltracodeOn,
  openaiBundledEffortTable,
  resolveRunEffort,
  ultracodeRunLevel,
  type EffortVendor,
  type ModelEffortTable,
} from '@chaos/types/effort';
import type { Vendor } from '../api/sessions';

/** One vendor-declared level as the catalogue carries it. */
export interface VendorEffortLevel {
  value: string;
  description?: string | null;
}

/** What a picker needs to know about the selected model. */
export interface ModelEffortSource {
  id: string;
  /** Vendor's own per-model list (gateway `efforts`); [] = no effort control. */
  efforts?: readonly VendorEffortLevel[];
  /** Vendor's default effort for this model. */
  defaultEffort?: string | null;
  /** The vendor marks this row as the account default model. */
  vendorDefault?: boolean;
  /** The pinned model an alias row runs today (gateway `resolvesTo`, the
   *  Claude picker's own statement) — its per-model gaps and default apply. */
  resolvesTo?: string;
}

/** One rendered option. `value` is EXACTLY what is stored and sent. */
export interface EffortChoice {
  value: string;
  /** The vendor's own name for the level. */
  label: string;
  /** The vendor's own one-line description, when it has one. */
  description?: string;
  /** True for the vendor's default level for this model. */
  isModelDefault?: boolean;
  /** True for the level CHAOS starts the vendor at when nothing is stored
   *  (SYSTEM_DEFAULT_EFFORT; set for GPT only: Extra high, founder 2026-10-04).
   *  Pickers badge THIS as "Default" and the vendor's own default level as
   *  "Model default", so the badge agrees with the level that really runs. */
  isSystemDefault?: boolean;
  /** `advanced` = the vendor keeps this level out of its main list, in a
   *  separate "More reasoning" group that carries a usage-limits warning. */
  group?: 'advanced';
}

/** The vendor's heading for the separate group (codex TUI: "More reasoning…"
 *  opening "Advanced Reasoning"). */
export const ADVANCED_REASONING_HEADING = 'More reasoning';

/** The vendor's warning line under that heading (codex TUI, verbatim). */
export const ADVANCED_REASONING_WARNING = 'Consumes usage limits faster';

// ── Claude (Anthropic) — claude 2.1.287 ────────────────────────────────────

/** The `/model` picker's own spelling: capitalised, except `xhigh` → "xHigh"
 *  (bundle: `Hn==="xhigh"?"xHigh":capitalize(Hn)` + " effort"). `ultracode`
 *  is the toggle's own name, used by chips that show a stored value. */
export const ANTHROPIC_EFFORT_LABEL: Readonly<Record<string, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'xHigh',
  max: 'Max',
  ultracode: 'Ultracode',
};

/** The `/effort` help's own one-line descriptions, verbatim (claude 2.1.287
 *  bundle `F={low:…,max:…}`). */
const ANTHROPIC_EFFORT_DESCRIPTION: Readonly<Record<string, string>> = {
  low: 'Quick, straightforward implementation',
  medium: 'Balanced approach with standard testing',
  high: 'Comprehensive implementation with extensive testing',
  xhigh: 'Extended reasoning with thorough analysis (on supported models)',
  max: 'Maximum capability with deepest reasoning (on supported models)',
};

/** The Ultracode toggle's own description in `/effort` (bundle `bot`). */
export const ULTRACODE_DESCRIPTION = 'Dynamic workflows on every task';

/** The CLI's `--effort` ladder, lowest first. Ultracode is not on it. */
export const ANTHROPIC_EFFORT_ORDER: readonly string[] = ANTHROPIC_EFFORT_LEVELS;

function anthropicChoices(model: ModelEffortSource | null): EffortChoice[] {
  if (model?.efforts && model.efforts.length === 0) return [];
  const table = anthropicEffortTable(model?.resolvesTo ?? model?.id ?? null);
  return table.levels.map((value) => ({
    value,
    label: ANTHROPIC_EFFORT_LABEL[value] ?? titleCase(value),
    description: ANTHROPIC_EFFORT_DESCRIPTION[value],
    isModelDefault: table.defaultEffort === value ? true : undefined,
  }));
}

// ── GPT (OpenAI) — codex 0.160.0 ───────────────────────────────────────────

/** The codex TUI's own names ("Select Reasoning Level" + "Advanced Reasoning"). */
export const OPENAI_EFFORT_LABEL: Readonly<Record<string, string>> = {
  none: 'None',
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
  ultra: 'Ultra',
};

/** Vendor's descriptions, verbatim from model/list (identical for every model). */
const OPENAI_EFFORT_DESCRIPTION: Readonly<Record<string, string>> = {
  low: 'Fast responses with lighter reasoning',
  medium: 'Balances speed and reasoning depth for everyday tasks',
  high: 'Greater reasoning depth for complex problems',
  xhigh: 'Extra high reasoning depth for complex problems',
};

/** What the codex 0.160.0 TUI prints for the two "Advanced Reasoning" rows,
 *  verbatim (evidence: codex-0.160.0-tui-captures.txt). These win over the
 *  shorter `model/list` strings for the same levels, so the CHAOS row reads
 *  exactly like the vendor's own. */
const OPENAI_ADVANCED_DESCRIPTION: Readonly<Record<string, string>> = {
  max: 'For difficult problems when quality matters more than speed · higher usage',
  ultra: 'For demanding work using multiple agents · highest usage',
};

/** FALLBACK tables (codex 0.160.0 bundled catalogue) — shared with the
 *  gateway so both sides fall back identically. */
export { OPENAI_BUNDLED_EFFORTS, OPENAI_BUNDLED_DEFAULT_MODEL };

function openaiChoices(model: ModelEffortSource | null, catalogue: readonly ModelEffortSource[]): EffortChoice[] {
  let source = model;
  if (source && source.id === 'default') {
    // CHAOS `default` = no `-m`: the CLI runs the account's default model.
    source = catalogue.find((m) => m.vendorDefault === true && m.id !== 'default') ?? null;
    if (!source) {
      const b = openaiBundledEffortTable('default');
      return toOpenaiChoices(b.levels.map((value) => ({ value })), b.defaultEffort);
    }
  }
  if (source?.efforts) {
    return toOpenaiChoices(source.efforts, source.defaultEffort ?? null);
  }
  // Not described by the live catalogue: the bundled table, else the four
  // levels every GPT model on record supports — never a level it may lack.
  const b = openaiBundledEffortTable(source?.id ?? 'default');
  return toOpenaiChoices(b.levels.map((value) => ({ value })), b.defaultEffort);
}

function toOpenaiChoices(levels: readonly VendorEffortLevel[], defaultEffort: string | null): EffortChoice[] {
  return levels.map((l) => {
    const advanced = OPENAI_ADVANCED_EFFORTS.includes(l.value);
    return {
      value: l.value,
      label: OPENAI_EFFORT_LABEL[l.value] ?? titleCase(l.value),
      description:
        (advanced ? OPENAI_ADVANCED_DESCRIPTION[l.value] : undefined) ??
        ((l.description ?? undefined) || OPENAI_EFFORT_DESCRIPTION[l.value]),
      isModelDefault: defaultEffort !== null && l.value === defaultEffort ? true : undefined,
      isSystemDefault: l.value === SYSTEM_DEFAULT_EFFORT.openai ? true : undefined,
      group: advanced ? ('advanced' as const) : undefined,
    };
  });
}

function titleCase(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/** The choices as the shared resolver's table. */
function tableOf(choices: readonly EffortChoice[]): ModelEffortTable {
  return {
    levels: choices.map((c) => c.value),
    defaultEffort: choices.find((c) => c.isModelDefault)?.value ?? null,
  };
}

// ── menu layout ────────────────────────────────────────────────────────────

/** How a picker lays the choices out. */
export interface EffortMenuLayout {
  /** The main list, in display order. */
  main: EffortChoice[];
  /** The separate "More reasoning" group, in display order ([] = none). */
  advanced: EffortChoice[];
  /** The vendor's one-line summary of the group ("Max and Ultra consume usage
   *  limits faster"); null when there is no group. */
  advancedSummary: string | null;
}

function joinNames(names: readonly string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The display order every picker uses.
 *
 *   GPT     Mirrors the codex TUI: the main list lowest first (Low · Medium ·
 *           High · Extra high), then the separate "More reasoning" group
 *           (Max · Ultra) below it. The group exists only when the model's
 *           vendor data has those levels.
 *   Claude  Highest first, as the CLI pickers here have always listed it; no
 *           group (Max is a plain level on Claude's own ladder).
 */
export function effortMenuLayout(vendor: Vendor | null | undefined, choices: readonly EffortChoice[]): EffortMenuLayout {
  if (vendor === 'openai') {
    const main = choices.filter((c) => c.group !== 'advanced');
    const advanced = choices.filter((c) => c.group === 'advanced');
    const names = advanced.map((c) => c.label);
    return {
      main,
      advanced,
      advancedSummary: advanced.length > 0
        ? `${joinNames(names)} ${advanced.length === 1 ? 'consumes' : 'consume'} usage limits faster`
        : null,
    };
  }
  return { main: [...choices].reverse(), advanced: [], advancedSummary: null };
}

/** The default badges a picker shows on one choice. GPT: "Default" sits on the
 *  level CHAOS starts at (Extra high); the vendor's own default for the model
 *  (Low on GPT-6.1 Sol) is "Model default". Claude keeps the vendor's own
 *  default as "Default" (unchanged). */
export function effortDefaultBadges(
  vendor: Vendor | null | undefined,
  c: EffortChoice,
): { isDefault: boolean; modelDefault: boolean } {
  if (vendor === 'openai') {
    return {
      isDefault: c.isSystemDefault === true,
      modelDefault: c.isModelDefault === true && c.isSystemDefault !== true,
    };
  }
  return { isDefault: c.isModelDefault === true, modelDefault: false };
}

/**
 * The stored effort a picker for `vendor` may treat as a deliberate choice.
 *
 * A session's stored effort is one column shared by every vendor's picker, and
 * Claude's own default is Max (the backend seeds every new Claude card with it,
 * and no picker ever wrote it). Without a guard, a card set to Max under Claude
 * and then moved to GPT would show — and send — Max, a level in GPT's
 * usage-limit-burning "More reasoning" group that nobody picked for GPT.
 *
 * Founder 2026-10-04: GPT's default is Extra high, and nothing may choose Max
 * or Ultra for GPT on its own. So a stored GPT advanced level (Max, Ultra)
 * counts as a deliberate GPT choice ONLY when there is positive evidence:
 *   - GPT's own picker wrote it (`writtenBy` === 'openai'), or
 *   - the session row itself belongs to GPT (`rowVendor` === 'openai'): the
 *     row was seeded by GPT or set by a GPT role default, i.e. explicit.
 * Everything else is dropped (null, so Extra high applies): a level another
 * vendor's picker wrote, AND a level nobody is known to have written on a
 * non-GPT row — which is exactly what a freshly created Claude card carries.
 * The failure direction is deliberate: a GPT Max that is wrongly forgotten
 * (new browser, cleared storage) costs one re-pick; a Claude Max wrongly
 * adopted burns usage limits.
 */
export function storedEffortForVendor(
  vendor: Vendor,
  stored: string | null | undefined,
  writtenBy: Vendor | null | undefined,
  rowVendor: Vendor | null | undefined,
): string | null | undefined {
  if (vendor !== 'openai' || !isOpenaiAdvancedEffort(stored)) return stored;
  if (writtenBy === 'openai') return stored;
  if (!writtenBy && rowVendor === 'openai') return stored;
  return null;
}

// ── public API ─────────────────────────────────────────────────────────────

/**
 * The levels to offer for `vendor` + `modelId`, lowest first, in the vendor's
 * order. `catalogue` is that vendor's merged preset list (it carries the
 * vendor data); pass [] when there is none. Empty result = no effort control.
 */
export function effortChoicesFor(
  vendor: Vendor,
  modelId: string | null | undefined,
  catalogue: readonly ModelEffortSource[] = [],
): EffortChoice[] {
  const model = modelId
    ? (catalogue.find((m) => m.id === modelId) ?? { id: modelId })
    : null;
  if (vendor === 'anthropic') return anthropicChoices(model);
  if (vendor === 'openai') return openaiChoices(model ?? { id: 'default' }, catalogue);
  return [];
}

/** The vendor's name for a stored value, whatever model; raw token if unknown. */
export function effortLabel(vendor: Vendor | null | undefined, value: string | null | undefined): string {
  if (!value) return '';
  if (vendor === 'openai') return OPENAI_EFFORT_LABEL[value] ?? titleCase(value);
  if (vendor === 'anthropic') return ANTHROPIC_EFFORT_LABEL[value] ?? titleCase(value);
  return ANTHROPIC_EFFORT_LABEL[value] ?? OPENAI_EFFORT_LABEL[value] ?? titleCase(value);
}

/**
 * The level the CLI really runs for a stored value — the pickers show THIS,
 * and it is the same answer the gateway sends (both call resolveRunEffort):
 *
 *   • the stored level, when this model has it;
 *   • Claude: an unsupported max/xhigh → high (the CLI's own downgrade);
 *   • otherwise — nothing stored, a level this model lacks (GPT-5.5 has no
 *     Max), another vendor's value (a GPT card holding Claude's `ultracode`) —
 *     the system default (`fallback`, default SYSTEM_DEFAULT_EFFORT: Claude
 *     max, GPT xhigh) when this model has it, else the model's own default;
 *   • Claude with Ultracode on: no --effort is passed, so the model's own
 *     default level (null when the model's default is not known).
 *
 * null = nothing names the level (render "Default"), or no effort control.
 */
export function effectiveEffort(
  vendor: Vendor,
  choices: readonly EffortChoice[],
  stored: string | null | undefined,
  fallback: string | null = SYSTEM_DEFAULT_EFFORT[vendor as EffortVendor] ?? null,
): string | null {
  if (choices.length === 0) return null;
  const table = tableOf(choices);
  if (isUltracodeOn(vendor as EffortVendor, stored)) return ultracodeRunLevel(table);
  return resolveRunEffort(vendor as EffortVendor, table, stored, fallback);
}

/** True when this vendor + model offers Claude Code's Ultracode toggle. */
export function ultracodeOffered(vendor: Vendor, choices: readonly EffortChoice[]): boolean {
  return vendor === 'anthropic' && choices.length > 0;
}

/** True when the stored value means "Ultracode on" for this vendor. */
export function ultracodeOn(vendor: Vendor | null | undefined, stored: string | null | undefined): boolean {
  return isUltracodeOn((vendor ?? null) as EffortVendor | null, stored);
}

/**
 * The value to store when the Ultracode toggle is switched OFF. The vendor's
 * own rule is "Ultracode off. Effort stays <level>", so the level it was
 * running at (the model's default) is kept explicitly; when that level is not
 * known, null = the system default, which the picker then shows.
 */
export function effortAfterUltracodeOff(choices: readonly EffortChoice[]): string | null {
  return ultracodeRunLevel(tableOf(choices));
}

/**
 * The effort value a turn should carry: `ultracode` while Claude's toggle is
 * on (the gateway turns it into the settings key and passes no --effort),
 * otherwise exactly the shown level (null = send nothing).
 */
export function effortToSend(
  vendor: Vendor,
  choices: readonly EffortChoice[],
  stored: string | null | undefined,
): string | null {
  if (choices.length === 0) return null;
  if (ultracodeOn(vendor, stored)) return ULTRACODE;
  return effectiveEffort(vendor, choices, stored);
}

/** The value a save should write: the effective one when the stored value no
 *  longer fits (normalise on the next save), otherwise unchanged. Claude's
 *  Ultracode stays on across a Claude model change. */
export function normalizedEffortForSave(
  vendor: Vendor,
  choices: readonly EffortChoice[],
  stored: string | null | undefined,
): string | null | undefined {
  if (!stored) return stored;
  if (choices.length === 0) return stored;
  if (ultracodeOn(vendor, stored)) return stored;
  if (choices.some((c) => c.value === stored)) return stored;
  return effectiveEffort(vendor, choices, stored);
}
